import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, FlaskConical, Wrench, GraduationCap, ShieldCheck } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import SEO from './SEO';
import Hero from './Hero';
import Reviews from './Reviews';
import PartnersLogos from './PartnersLogos';

const processSteps = [
  { icon: ClipboardList, step: 1, title: 'Site Assessment', description: 'We evaluate your facility and collaborate with your teams to build a precise installation plan.' },
  { icon: FlaskConical, step: 2, title: 'Trials', description: 'We install demo equipment so your team can validate fit, usability, and workflow before committing.' },
  { icon: Wrench, step: 3, title: 'Installation', description: 'Certified technicians handle every detail — from ceiling arms to complete room configurations.' },
  { icon: GraduationCap, step: 4, title: 'Training', description: 'We train your clinical and support staff on proper use, safety, and best practices from day one.' },
  { icon: ShieldCheck, step: 5, title: 'Support', description: 'We stay with you after installation — maintenance, troubleshooting, and service when you need it.' },
];

const MobileStep: React.FC<{ step: typeof processSteps[0]; index: number; visible: boolean }> = ({ step, index, visible }) => (
  <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.1rem 1.25rem', background: 'rgba(255,255,255,0.03)', opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-60px)', transition: `opacity 0.5s ease ${index * 0.12}s, transform 0.5s ease ${index * 0.12}s` }}>
    <div style={{ background: 'rgba(147,197,253,0.1)', borderRadius: '8px', padding: '0.5rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px' }}>
      <step.icon size={18} color="var(--color-primary-pale)" strokeWidth={2} />
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-faint)', letterSpacing: '0.08em', flexShrink: 0, width: '52px' }}>STEP {index + 1}</span>
      <span style={{ fontWeight: 600, color: 'var(--color-surface-alt)', fontSize: '0.9rem' }}>{step.title}</span>
    </div>
  </div>
);

const JVPDifference: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0, rootMargin: '0px 0px -300px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ background: 'linear-gradient(135deg, var(--color-navy-deep) 0%, var(--color-navy) 100%)', padding: '5rem 2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 style={{ color: 'var(--color-white)', fontSize: '2.25rem', fontWeight: 700, marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>The JVP Difference</h2>
          <p style={{ color: 'var(--color-muted-on-dark-light)', fontSize: '1.05rem', maxWidth: '700px', margin: '0 auto', lineHeight: 1.7 }}>From the first site visit to long-term support, every step is handled with white-glove care.</p>
        </div>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' }}>
            {processSteps.map((s, i) => (
              <MobileStep key={s.step} step={s} index={i} visible={visible} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1px', background: 'rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            {processSteps.map((s, i) => (
              <div key={s.step} style={{
                background: 'rgba(255,255,255,0.03)',
                padding: '2.5rem 1.75rem',
                textAlign: 'center',
                position: 'relative',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0)' : 'translateX(-120px)',
                transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
                  <s.icon size={32} color="var(--color-primary-pale)" strokeWidth={1.5} />
                </div>
                <div style={{ fontWeight: 600, color: 'var(--color-surface-alt)', fontSize: '0.95rem', lineHeight: 1.5 }}>{s.title.trim()}</div>
                {i < processSteps.length - 1 && (
                  <div style={{ position: 'absolute', right: '-14px', top: '50%', transform: visible ? 'translateY(-50%)' : 'translateX(-60px) translateY(-50%)', color: 'var(--color-primary-light)', fontSize: '1.4rem', fontWeight: 300, zIndex: 1, opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${i * 0.12 + 0.06}s` }}>›</div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ textAlign: 'center', marginTop: '3rem', opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)', transition: 'opacity 0.5s ease 0.65s, transform 0.5s ease 0.65s' }}>
          <Link to="/services" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.85rem 2.25rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', display: 'inline-block', letterSpacing: '0.01em' }}>See How We Work →</Link>
        </div>
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const isMobile = useIsMobile();
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "JVP Health Tech Solutions",
    "url": "https://www.jvphealthtechsolutions.com",
    "logo": "https://www.jvphealthtechsolutions.com/images/logos/jvp-logo.png",
    "description": "Professional healthcare equipment installation services for arms, carts, desks, and complete room setups.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "info@jvphealthtechsolutions.com"
    },
    "sameAs": [
      "https://www.linkedin.com/in/joe-penwell-407bb9271"
    ]
  };

  return (
    <>
      <SEO
        title="JVP Health Tech Solutions | Healthcare Equipment Installation"
        description="JVP Health Tech Solutions provides nationwide healthcare equipment installation — workstations, ceiling arms, mobile carts, and virtual nursing stations."
        keywords="healthcare equipment installation, hospital workstation installation, ceiling mounted arm, medical cart installation, virtual nursing station, ADA compliant hospital installation, JCO compliant, nationwide healthcare installation"
        url="https://www.jvphealthtechsolutions.com"
        structuredData={structuredData}
      />
      <Hero />

      {/* The JVP Difference */}
      <JVPDifference />

      {/* Products promo — text left, image right */}
      <div style={{ background: 'var(--color-white)', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '2rem' : '4rem', alignItems: 'center' }}>
          <div style={{ order: isMobile ? 2 : 0 }}>
            {/* <p style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Equipment We Install</p> */}
            <h2 style={{ color: 'var(--color-text)', fontSize: '2rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '1.25rem' }}>The Right Equipment. Installed Right. Every Time.</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '1.75rem' }}>From hospital workstations and patient monitor arms to mobile carts, virtual nursing stations, and pharmacy solutions — we work with industry-leading manufacturers to source, configure, and install the right equipment for your facility.</p>
            <Link to="/products" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.85rem 2.25rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', display: 'inline-block' }}>View Equipment →</Link>
          </div>
          <div style={{ borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: '380px' }}>
            <picture>
              <source srcSet="/images/home/install-d9f6f4c5.avif" type="image/avif" />
              <img src="/images/home/install-d9f6f4c5.jpg" alt="Technician installing ceiling-mounted medical equipment in a hospital room" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
            </picture>
          </div>
        </div>
      </div>

      {/* Our Work promo — image left, text right */}
      <div style={{ background: 'var(--color-surface)', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '2rem' : '4rem', alignItems: 'center' }}>
          <div style={{ borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: '380px' }}>
            <picture>
              <source srcSet="/images/home/projects-afb3b90d.avif" type="image/avif" />
              <img src="/images/home/projects-afb3b90d.jpg" alt="Completed healthcare equipment installations across multiple hospital facilities" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
            </picture>
          </div>
          <div>
            {/* <p style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Real Results</p> */}
            <h2 style={{ color: 'var(--color-text)', fontSize: '2rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '1.25rem' }}>Real Projects. Real Facilities. Real Results.</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '1.75rem' }}>Browse our project gallery, read reviews from healthcare professionals, and see the facilities we've served — from single-room installs to full national deployments.</p>
            <Link to="/work" style={{ background: 'var(--color-navy)', color: 'white', padding: '0.85rem 2.25rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', display: 'inline-block' }}>View Our Work →</Link>
          </div>
        </div>
      </div>

      {/* About Us */}
      <div style={{ background: 'linear-gradient(135deg, var(--color-navy-deep) 0%, var(--color-navy) 100%)', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '2.5rem' : '5rem', alignItems: 'center' }}>
          <div>
            {/* <p style={{ color: 'var(--color-primary-pale)', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Who We Are</p> */}
            <h2 style={{ color: 'var(--color-white)', fontSize: '2rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '1.25rem' }}>Built for Healthcare. Focused on Getting It Right.</h2>
            <p style={{ color: 'var(--color-muted-on-dark-light)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '1rem' }}>
              JVP Health Tech Solutions is a nationwide healthcare equipment installation company. We specialize in the full lifecycle of clinical technology — from site assessment and trials through installation, training, and long-term support.
            </p>
            <p style={{ color: 'var(--color-muted-on-dark-light)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '2rem' }}>
              Every installation we perform meets ADA accessibility requirements and Joint Commission (JCO) standards — so your facility is compliant, functional, and ready for what's next.
            </p>
            <Link to="/about" style={{ background: 'white', color: 'var(--color-navy)', padding: '0.85rem 2.25rem', borderRadius: '50px', fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem', display: 'inline-block' }}>Meet the Team →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {[
              { stat: 'Nationwide', label: 'Service Coverage' },
              { stat: 'ADA & JCO', label: 'Compliant Installs' },
              { stat: '750+', label: 'Projects Completed' },
              { stat: 'End-to-End', label: 'Service Model' },
            ].map(({ stat, label }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: isMobile ? '1.25rem 0.75rem' : '2rem 1.5rem', textAlign: 'center' }}>
                <div style={{ color: 'var(--color-white)', fontSize: isMobile ? '1.2rem' : '1.6rem', fontWeight: 700, marginBottom: '0.4rem' }}>{stat}</div>
                <div style={{ color: 'var(--color-muted-on-dark-light)', fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PartnersLogos />
      <Reviews />
    </>
  );
};

export default Home;
